import { Link } from '@tanstack/react-router';
import { Loader2, Mail, PersonStanding, Phone } from 'lucide-react';

import {
  Label,
  useGetAccountsReceivableStudentGrouping,
} from '~entities/dashboard/api/get-accounts-receivable-student-grouping';
import { useGetManualAccountsReceivableStudentGrouping } from '~entities/dashboard/api/get-manual-accounts-receivable-student-grouping';

import { formatCurrencyAmount } from '~features/new-dashboard/helpers/format-currency';

import { useGetCurrency } from '~shared/api/currency-api';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';

interface AccountReceivableModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, lists session-scoped receivables (class/event/session-linked). Omit when `manualInvoicesOnly` is true. */
  sessionId?: string;
  /** Manual invoices are studio-wide and not tied to the selected session. */
  manualInvoicesOnly?: boolean;
}

export function AccountReceivableModal({
  open,
  onOpenChange,
  sessionId,
  manualInvoicesOnly = false,
}: AccountReceivableModalProps) {
  const { data: currencyDetails } = useGetCurrency();
  const currencySymbol = currencyDetails?.symbol ?? '$';

  const { data: sessionScopedGrouping, isFetching: isSessionScopedFetching } =
    useGetAccountsReceivableStudentGrouping({
      sessionId: sessionId ?? '',
      enabled: !manualInvoicesOnly && !!sessionId,
    });

  const { data: manualGrouping, isFetching: isManualFetching } =
    useGetManualAccountsReceivableStudentGrouping({
      enabled: manualInvoicesOnly,
    });

  const accountsReceivableStudentGrouping = manualInvoicesOnly
    ? manualGrouping
    : sessionScopedGrouping;
  const isAccountsReceivableStudentGroupingLoading = manualInvoicesOnly
    ? isManualFetching
    : isSessionScopedFetching;

  const formatMoney = (amount: number | undefined) =>
    amount == null
      ? `${currencySymbol}0.00`
      : `${currencySymbol}${formatCurrencyAmount(amount)}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex max-h-[calc(100svh-2rem)] w-[calc(100%-2rem)] flex-col overflow-y-auto overscroll-contain p-4 sm:max-w-5xl sm:p-6'>
        <DialogHeader className='pr-8 text-left'>
          <DialogTitle className='text-xl font-semibold text-[#000814] sm:text-2xl'>
            {manualInvoicesOnly
              ? 'Manual invoice receivables'
              : 'Accounts Receivable'}
          </DialogTitle>
          <DialogDescription>
            {manualInvoicesOnly
              ? 'Outstanding balances on manual invoices (not linked to a class or session), organized by due date.'
              : 'All outstanding accounts receivable balances owed by families, organized by due date.'}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          {isAccountsReceivableStudentGroupingLoading ? (
            <div className='flex items-center justify-center'>
              <Loader2 className='size-10 animate-spin' />
            </div>
          ) : (
            accountsReceivableStudentGrouping?.map((entry) => {
              const totalByAgingLabel = new Map(
                entry.groups.map((group) => [group.label, group.total]),
              );
              const studentTotal = entry.groups.reduce(
                (acc, group) => acc + group.total,
                0,
              );

              return (
                <div
                  key={entry.studentId}
                  className='rounded-xl border border-border/50 p-3 transition-all duration-200 hover:border-[#FFC300]/30 hover:shadow-md sm:p-5'
                >
                  <div className='mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between'>
                    <div className='min-w-0'>
                      <Link
                        to={`/students/$studentId/details`}
                        params={{ studentId: entry.studentId }}
                        className='w-fit break-words text-base font-semibold text-[#000814] hover:underline sm:text-lg'
                      >
                        {entry.studentName}
                      </Link>
                      <div className='mt-1 flex flex-col gap-1 text-sm text-[#6B7280] sm:flex-row sm:flex-wrap sm:items-center sm:gap-3'>
                        <Link
                          to={`/family`}
                          search={{ parentId: entry.parentId }}
                          className='flex min-w-0 items-center gap-1 hover:underline'
                        >
                          <PersonStanding className='size-3.5 shrink-0' />
                          <span className='break-words'>{entry.parentName}</span>
                        </Link>
                        <span className='flex min-w-0 items-center gap-1'>
                          <Mail className='size-3.5 shrink-0' />
                          <span className='break-all'>{entry.email}</span>
                        </span>
                        <span className='flex min-w-0 items-center gap-1'>
                          <Phone className='size-3.5 shrink-0' />
                          <span className='break-words'>{entry.phone}</span>
                        </span>
                      </div>
                    </div>
                    <div className='text-left sm:shrink-0 sm:text-right'>
                      <p className='text-xl font-bold tabular-nums text-[#000814] sm:text-2xl'>
                        {formatMoney(studentTotal)}
                      </p>
                    </div>
                  </div>

                  <div className='grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-4'>
                    <div className='rounded-lg bg-green-50 p-3'>
                      <p className='mb-1 text-xs font-medium text-green-700'>
                        0-30 Days
                      </p>
                      <p className='text-base font-semibold tabular-nums text-green-900 sm:text-lg'>
                        {formatMoney(totalByAgingLabel.get(Label.Days0To30))}
                      </p>
                    </div>
                    <div className='rounded-lg bg-yellow-50 p-3'>
                      <p className='mb-1 text-xs font-medium text-yellow-700'>
                        31-60 Days
                      </p>
                      <p className='text-base font-semibold tabular-nums text-yellow-900 sm:text-lg'>
                        {formatMoney(totalByAgingLabel.get(Label.Days31To60))}
                      </p>
                    </div>
                    <div className='rounded-lg bg-orange-50 p-3'>
                      <p className='mb-1 text-xs font-medium text-orange-700'>
                        61-90 Days
                      </p>
                      <p className='text-base font-semibold tabular-nums text-orange-900 sm:text-lg'>
                        {formatMoney(totalByAgingLabel.get(Label.Days61To90))}
                      </p>
                    </div>
                    <div className='rounded-lg bg-red-50 p-3'>
                      <p className='mb-1 text-xs font-medium text-red-700'>
                        90+ Days
                      </p>
                      <p className='text-base font-semibold tabular-nums text-red-900 sm:text-lg'>
                        {formatMoney(totalByAgingLabel.get(Label.Days90Plus))}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
